import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { faPrint } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Toast,
} from '@repo/videa-components/src/index';
import { useSetAtom } from 'jotai';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import type {
  GetZuubInsuranceDetailResponseV3,
  ZuubPatientData,
} from '../../@types/zuub/contracts';
import { PatientNamePrivacy } from '../../common-components/composed/PatientNamePrivacy';
import { useGenerateBenefitsPdf } from '../../react-query/rcm/useGenerateBenefitsPdf';
import { useHasBenefitsWritebackSupport } from '../../react-query/rcm/useHasBenefitsWritebackSupport';
import { usePushBenefitsToPMS } from '../../react-query/rcm/usePushBenefitsToPMS';
import { useGetZuubInsuranceDetail } from '../../react-query/useGetZuubInsuranceDetail';
import { toBusinessName } from '../../utils/string';
import { PrivacyButton } from '../patientList/PrivacyButton';
import { useShouldHideHuddle } from '../patientList/useShouldHideHuddle';

import { selectedPlanAtom } from './atoms';
import {
  getPlanStatusByPayerName,
  getPlanTypesForPayer,
  getSelectedPlan,
  getUniquePayerNames,
  isPlanActive,
} from './helpers';

interface BenefitsPageTopbarProps {}

export const BenefitsPageTopbar = ({}: BenefitsPageTopbarProps) => {
  const history = useHistory();
  const { t } = useLingui();
  const hideHuddle = useShouldHideHuddle();
  const { patientId } = useParams<{ patientId: string }>();

  // Fetch detailed insurance data for this patient via API
  const { data: insuranceDetailData, isLoading: isLoadingInsuranceDetailData } =
    useGetZuubInsuranceDetail({
      pmsPatientId: patientId,
      enabled: !!patientId,
    });

  // Convert the API response to ZuubPatientData format for helper functions
  const currentPatientData: ZuubPatientData | null = useMemo(() => {
    if (!insuranceDetailData?.found) return null;
    const v3Data = insuranceDetailData as GetZuubInsuranceDetailResponseV3;
    return {
      pmsPatientId: v3Data.pmsPatientId,
      patientName: v3Data.patientName,
      appointmentDate: v3Data.appointmentDate,
      lastRun: v3Data.lastRun,
      hasMultiplePlans: v3Data.hasMultiplePlans,
      totalPlans: v3Data.totalPlans,
      detailedData: v3Data.detailedData,
    };
  }, [insuranceDetailData]);

  const defaultPlan = currentPatientData?.detailedData?.[0];
  const setSelectedPlan = useSetAtom(selectedPlanAtom);

  const {
    mutate: generatePdf,
    isLoading: isPdfLoading,
    reset: resetPdfMutation,
  } = useGenerateBenefitsPdf();
  const {
    mutate: pushToPMS,
    isLoading: isPushingToPMS,
    reset: resetPushToPMSMutation,
  } = usePushBenefitsToPMS();
  const hasBenefitsWritebackSupport =
    useHasBenefitsWritebackSupport().data ?? false;
  const shouldShowPushToPMS = hasBenefitsWritebackSupport;

  // Initialize payer name from the first plan
  const initialPayerName = defaultPlan?.payerName || '';

  // State for selected plan name and plan type
  const [selectedPayerName, setSelectedPayerName] =
    useState<string>(initialPayerName);

  // Initialize plan type from a plan that matches the initial payer name
  // Use lazy initializer to ensure it only runs once
  const [selectedPlanType, setSelectedPlanType] = useState<string>(() => {
    if (!currentPatientData?.detailedData || !initialPayerName) return '';
    const matchingPlan = currentPatientData.detailedData.find(
      (plan) => plan.payerName === initialPayerName,
    );
    return matchingPlan?.plan?.planType || '';
  });

  // Get the currently selected plan based on dropdown selections
  const currentPlan = getSelectedPlan(
    currentPatientData,
    selectedPayerName,
    selectedPlanType,
  );

  // Derive patient name from Zuub data
  const patientName = currentPatientData?.patientName || 'Unknown Patient';

  // Get unique payer names and plan types from available data
  const availablePayerNames = useMemo(
    () => getUniquePayerNames(currentPatientData),
    [currentPatientData],
  );

  // Get plan types available for the currently selected payer
  const availablePlanTypes = useMemo(
    () => getPlanTypesForPayer(currentPatientData, selectedPayerName),
    [currentPatientData, selectedPayerName],
  );

  const handleBackToPatientList = () => {
    history.push('/auto-verify');
  };

  const handleGeneratePdf = useCallback(() => {
    if (!currentPlan) return;

    generatePdf(
      {
        unifiedAppointmentId: currentPlan.unifiedAppointmentId,
        patientName: patientName,
      },
      {
        onSuccess: () => {
          toast(
            <Toast
              variant="success"
              title={t`PDF generated successfully!`}
              description={t`Your benefits summary PDF has been downloaded.`}
            />,
            {
              closeButton: true,
              position: 'top-right',
              style: { width: '460px' },
            },
          );
          resetPdfMutation();
        },
        onError: (error) => {
          console.error('Failed to generate PDF:', error);
          toast(
            <Toast
              variant="error"
              title={t`PDF generation failed`}
              description={t`Failed to generate PDF. Please try again or contact support if the problem persists.`}
            />,
            {
              closeButton: true,
              position: 'top-right',
              style: { width: '460px' },
            },
          );
          resetPdfMutation();
        },
      },
    );
  }, [currentPlan, patientName, generatePdf, resetPdfMutation, t]);

  // Handler for pushing to PMS
  const handlePushToPMS = useCallback(() => {
    if (!currentPlan) return;

    pushToPMS(
      {
        unifiedAppointmentId: currentPlan.unifiedAppointmentId,
      },
      {
        onSuccess: () => {
          toast(
            <Toast
              variant="success"
              title={t`Success`}
              description={t`Successfully pushed to PMS`}
            />,
            {
              closeButton: true,
              position: 'top-right',
            },
          );
          resetPushToPMSMutation();
        },
        onError: (error) => {
          console.error('Error pushing to PMS:', error);
          toast(
            <Toast
              variant="error"
              title={t`Error`}
              description={t`Failed to push to PMS. Please try again.`}
            />,
            {
              closeButton: true,
              position: 'top-right',
            },
          );
          resetPushToPMSMutation();
        },
      },
    );
  }, [currentPlan, pushToPMS, resetPushToPMSMutation, t]);

  // Update the selected plan atom when selections change
  useEffect(() => {
    setSelectedPlan(currentPlan);
  }, [currentPlan, setSelectedPlan]);

  // Initialize or reset plan type when payer name changes or when data becomes available
  useEffect(() => {
    if (selectedPayerName && availablePlanTypes.length > 0) {
      // If plan type is empty or not in the available list, set it to the first available
      if (!selectedPlanType || !availablePlanTypes.includes(selectedPlanType)) {
        setSelectedPlanType(availablePlanTypes[0]);
      }
    } else if (selectedPayerName && availablePlanTypes.length === 0) {
      // If there are no plan types for this payer, clear the selection
      setSelectedPlanType('');
    } else if (!selectedPayerName && currentPatientData?.detailedData) {
      // If payer name is empty but we have data, try to initialize from first plan
      const firstPlan = currentPatientData.detailedData[0];
      if (firstPlan?.payerName && !selectedPayerName) {
        setSelectedPayerName(firstPlan.payerName);
      }
    }
  }, [
    selectedPayerName,
    availablePlanTypes,
    selectedPlanType,
    currentPatientData,
  ]);

  return (
    <div className="vai-bg-white vai-px-4 vai-py-6 vai-shadow-sm vai-z-10 vai-h-[124px]">
      {/* First Row - Back to Patient List and Action Buttons */}
      <div className="vai-flex vai-items-center vai-justify-between vai-h-8 vai-mb-4">
        <button
          onClick={handleBackToPatientList}
          className="vai-flex vai-items-center vai-gap-2 vai-text-gray-700 hover:vai-text-gray-900 vai-transition-colors vai-duration-200"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="vai-text-sm" />
          <span className="vai-text-sm vai-font-medium">
            {hideHuddle ? (
              <Trans>All Patients</Trans>
            ) : (
              <Trans>Dashboard</Trans>
            )}
          </span>
        </button>
      </div>

      {/* Second Row - Patient Name, Selects, and Actions */}
      <div className="vai-flex vai-items-center vai-justify-between vai-h-10">
        {/* Left side - Patient Name and Selects */}
        <div className="vai-flex vai-items-center vai-gap-4">
          {/* Patient Name - aligned with Back to Patient List */}
          <h1 className="vai-text-xl vai-font-bold vai-text-gray-900 vai-whitespace-nowrap vai-ml-4">
            <PatientNamePrivacy name={patientName} noWrap={true} />
          </h1>

          {/* Vertical Divider */}
          <div className="vai-h-10 vai-w-px vai-bg-gray-300 vai-mx-2"></div>

          {/* Insurance Provider Select */}
          <Select
            value={toBusinessName(selectedPayerName)}
            onValueChange={(value) => {
              // Find the original payer name from the formatted value
              const originalPayerName = availablePayerNames.find(
                (name) => toBusinessName(name) === value,
              );
              if (originalPayerName) {
                setSelectedPayerName(originalPayerName);
              }
            }}
          >
            <SelectTrigger className="vai-w-auto vai-whitespace-nowrap [&>span]:vai-mr-2">
              <SelectValue placeholder={t`Select insurance provider`} />
            </SelectTrigger>
            <SelectContent>
              {availablePayerNames.map((payerName) => {
                const formattedName = toBusinessName(payerName);

                // Find the plan to get order and status
                const plan = currentPatientData?.detailedData?.find(
                  (planData) => planData.payerName === payerName,
                );
                const planStatus = plan?.plan?.status || null;
                const isActive = isPlanActive(planStatus);
                const isPrimary = plan?.order === 0;

                return (
                  <SelectItem
                    key={payerName}
                    value={formattedName}
                    disabled={!isActive}
                  >
                    <div className="vai-flex vai-items-center vai-gap-2">
                      {formattedName}
                      {(isPrimary || !isActive) && (
                        <span className="vai-text-gray-500 vai-text-sm">
                          {' '}
                          {isPrimary && (
                            <>
                              (<Trans>Primary</Trans>)
                            </>
                          )}
                          {isPrimary && !isActive && ' '}
                          {!isActive && (
                            <>
                              (<Trans>Inactive</Trans>)
                            </>
                          )}
                        </span>
                      )}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          {/* Plan Type Select - only show if user has plan types under currently selected payer */}
          {availablePlanTypes.length > 0 && (
            <Select
              value={selectedPlanType}
              onValueChange={setSelectedPlanType}
            >
              <SelectTrigger className="vai-w-auto">
                <SelectValue placeholder={t`Plan type`} />
              </SelectTrigger>
              <SelectContent>
                {availablePlanTypes.map((planType) => {
                  return (
                    <SelectItem key={planType} value={planType}>
                      {planType.toUpperCase()}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Right side - Push to PMS, Privacy Button and Print Button */}
        <div className="vai-flex vai-items-center vai-gap-2">
          {/* Push to PMS Button */}
          {shouldShowPushToPMS && (
            <Button
              disabled={!currentPlan}
              loading={isPushingToPMS}
              onClick={handlePushToPMS}
            >
              <Trans>Push to PMS</Trans>
            </Button>
          )}
          <PrivacyButton source="AUTOVERIFY" />
          {/* Print Button */}
          <Button
            disabled={!currentPlan}
            loading={isPdfLoading}
            title={t`Print`}
            variant="outline"
            onClick={handleGeneratePdf}
          >
            <FontAwesomeIcon className="vai-text-zinc-900" icon={faPrint} />
          </Button>
        </div>

        {/* Right side - Search Input */}
        {/* <div className="vai-relative vai-w-80">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="vai-absolute vai-left-3 vai-top-1/2 vai-transform vai--translate-y-1/2 vai-text-gray-400 vai-text-sm vai-pointer-events-none vai-z-10"
          />
          <Input
            placeholder="Search ADA code or service"
            className="vai-pl-8"
          />
        </div> */}
      </div>
    </div>
  );
};
